<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">Companies</h1>
      </template>
      <template #actions>
        <page-header-action
          tag="a"
          href="#"
          @click.prevent="openForm"
          v-if="hasPermission('companies.create')"
        >
          Add Company
        </page-header-action>
        <page-header-action
          tag="a"
          href="#"
          @click.prevent="sendToCreateView"
          v-if="hasPermission('companies.create')"
        >
          Find Company
        </page-header-action>
      </template>
    </page-header>

    <div class="row">
      <div class="col-lg-12">
        <div class="card mb-3">
          <div class="loading-overlay" v-if="loading"></div>

          <div class="card-header">
            <div style="width: 100%; margin: 0;">
              <label for="type_id" style="margin-top: 0;">Type</label>

              <select
                name="type_id"
                id="type_id"
                class="form-control"
                :value="filters.type_id"
                @change="setType($event.target.value)"
              >
                <option value="">All Types</option>
                <option
                  :key="index"
                  :value="type.id"
                  v-for="(type, index) in types"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Company Types</th>
                  <th>Company Address</th>
                  <th>Has Cross Hire Rates?</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                <tr v-bind:key="index" v-for="(company, index) in companies">
                  <td v-text="company.name"></td>
                  <td>
                    <span
                      v-bind:key="index"
                      v-for="(type, index) in company.types"
                      class="label label-primary"
                      v-text="type.label"
                    ></span>
                  </td>
                  <td>
                    {{ company.address_line_one }}, {{ company.city }}.
                    {{ company.postcode }}
                  </td>

                  <td>
                    <span
                      class="badge badge-success"
                      v-if="!!company.cross_hire_rates.length"
                    >
                      Yes
                    </span>
                    <span class="badge badge-danger" v-else>
                      No
                    </span>
                  </td>

                  <td>
                    <button
                      class="btn btn-primary btn-sm"
                      v-on:click.prevent="viewCompany(company)"
                      v-if="
                        hasPermission('companies.read') &&
                          !hasPermission('companies.update')
                      "
                    >
                      View
                    </button>
                    <button
                      class="btn btn-primary btn-sm"
                      v-on:click.prevent="updateCompany(company)"
                      v-if="hasPermission('companies.update')"
                    >
                      Edit
                    </button>
                    <!--                    <button-->
                    <!--                      class="btn btn-primary btn-sm"-->
                    <!--                      v-on:click.prevent="viewContacts(company)"-->
                    <!--                      v-if="hasPermission('companies.update')"-->
                    <!--                    >-->
                    <!--                      Contacts-->
                    <!--                    </button>-->
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <modal :show="modal" :large="true" v-on:close="cancelAddCompany">
      <h4 slot="title">{{ editingIdentifier ? 'Update' : 'Add' }} Company</h4>
      <div>
        <inputbox
          v-model="form.name"
          name="name"
          label="Company Name"
          :required="true"
          :error-message="form.errors.get('name')"
          helper="Please enter the company name"
          :show-error="form.errors.has('name')"
          :readonly="readonly"
        ></inputbox>

        <inputbox
          v-model="form.email_address"
          name="email_address"
          label="Email Address"
          :error-message="form.errors.get('email_address')"
          helper="Please enter the company email address"
          :show-error="form.errors.has('email_address')"
          :readonly="readonly"
        ></inputbox>

        <inputbox
          v-model="form.telephone_number"
          name="telephone_number"
          label="Telephone Number"
          :error-message="form.errors.get('telephone_number')"
          helper="Please enter the company telephone number"
          :show-error="form.errors.has('telephone_number')"
          :readonly="readonly"
        ></inputbox>

        <div>
          <address-lookup
            :address="address"
            v-on:address="setAddress"
            v-if="!readonly"
          ></address-lookup>

          <div class="form-group" v-else>
            <div class="col-md-4">
              Address
            </div>

            <div class="col-md-8">
              {{ formatted_address }}
            </div>
          </div>
        </div>

        <div class="form-group has-helper">
          <div class="col-md-4">
            <label class="control-label">Types</label>
            <p class="help-block">Please select the company types</p>
          </div>
          <div class="col-md-8">
            <multiselect
              v-model="form.types"
              :options="types"
              :multiple="true"
              track-by="id"
              label="label"
              placeholder="Choose..."
              v-if="!readonly"
            ></multiselect>

            <span v-else>
              <span
                v-for="(type, index) in form.types"
                :key="index"
                class="badge badge-primary"
              >
                {{ type.label }}
              </span>
            </span>
          </div>
        </div>

        <div class="form-group" v-if="!readonly">
          <div class="col-md-4">
            <label class="control-label">
              Exclude From Non-Fault Claim Stats?
            </label>
          </div>
          <div class="col-md-8">
            <toggleswitch
              :labels="true"
              name="exclude_from_non_fault"
              helper="Should Accident claims for this company be removed from the Non-Fault claim stats?"
              v-model="form.exclude_from_non_fault"
            ></toggleswitch>
          </div>
        </div>

        <div class="form-group">
          <div class="col-md-4">
            <label class="control-label">
              Has Cross Hire Rates?
            </label>
          </div>
          <div class="col-md-8">
            <toggleswitch
              :labels="true"
              name="document.identifier"
              helper="Do you want to set cross hire rates for the company?"
              v-model="hasCrossHireRates"
              @change="toggleRate"
              v-if="!readonly"
            ></toggleswitch>

            <span
              v-else
              class="badge"
              :class="{
                'badge-success': hasCrossHireRates,
                'badge-danger': !hasCrossHireRates
              }"
            >
              {{ hasCrossHireRates ? 'Yes' : 'No' }}
            </span>
          </div>
        </div>

        <fieldset v-if="hasCrossHireRates">
          <legend>Cross Hire Tariff</legend>

          <tariff :rates-form="ratesForm" :readonly="readonly"></tariff>
          <!--          <currencyinput-->
          <!--            name="cross_hire_rate"-->
          <!--            :label="abiGroupsMap[rate.group_id]"-->
          <!--            v-model="rate.rate"-->
          <!--            :key="`cross-hire-rate-${index}`"-->
          <!--            v-for="(rate, index) in form.cross_hire_rates"-->
          <!--            :readonly="readonly"-->
          <!--          ></currencyinput>-->
        </fieldset>
      </div>
      <div
        class="footer"
        slot="footer"
        v-if="
          hasPermission('companies.update') || hasPermission('companies.create')
        "
      >
        <button
          class="btn btn-danger float-left"
          v-on:click.prevent="cancelAddCompany"
        >
          Close
        </button>

        <form-button :indicator="saving" @clicked="saveCompany">
          Save Company
        </form-button>
      </div>
    </modal>
  </div>
</template>

<script>
import Tariff from '@/components/Modules/Shared/Tariff.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  components: {
    Tariff,
    PageHeader,
    PageHeaderAction
  },
  data() {
    return {
      companies: [],
      loading: true,
      showUpdateModal: false,
      modal: false,
      readonly: false,
      form: new window.Form({
        name: null,
        email_address: null,
        telephone_number: null,
        address_line_one: null,
        address_line_two: null,
        city: null,
        postcode: null,
        types: [],
        exclude_from_non_fault: false,
        cross_hire_rates: []
      }),
      ratesForm: new window.Form({
        rates: [],
        storage_daily_rate: null,
        storage_cap: null
      }),
      filters: {
        type_id: ''
      },
      nonGtaRate: [],
      crossHireTariffId: null,
      hasCrossHireRates: false,
      indicator: false,
      saving: false,
      editingIdentifier: null,
      abiGroupsMap: {}
    };
  },

  computed: {
    address() {
      return {
        address_line_one: this.form.address_line_one,
        address_line_two: this.form.address_line_two,
        city: this.form.city,
        postcode: this.form.postcode
      };
    },

    formatted_address() {
      return [
        this.form.address_line_one,
        this.form.address_line_two,
        this.form.city,
        this.form.postcode
      ]
        .filter(v => !!v && v !== '')
        .join(', ');
    },

    types() {
      return this.$store.getters['CompanyTypes/all'];
    },

    groups() {
      return this.$store.getters['VehicleGroups/all'];
    }
  },

  mounted() {
    this.$store.commit('CompanyTypes/retrieve');
    this.$store.commit('CreditHireCharges/retrieve');
    this.$store.commit('CreditHireChargeTypes/retrieve');
    this.$store.commit('VehicleGroups/retrieve');

    this.findCompanies();
    this.fetchRates();
  },

  methods: {
    setAddress(address) {
      this.form.address_line_one = address.address_line_one;
      this.form.address_line_two = address.address_line_two;
      this.form.city = address.city;
      this.form.postcode = address.postcode;
    },

    openForm() {
      this.modal = true;
    },

    cancelAddCompany() {
      this.modal = false;
      this.form.reset();
      this.editingIdentifier = null;
      this.hasCrossHireRates = false;
    },

    saveCompany() {
      let url = '/companies';
      let method = 'post';

      if (this.editingIdentifier) {
        url += `/${this.editingIdentifier}`;
        method = 'put';
      }

      this.saving = true;

      window.Ajax[method](url, this.form.data())
        .then(response => {
          window.swal(
            'Success!',
            'The company was saved successfully!',
            'success'
          );

          this.saving = false;

          if (!this.editingIdentifier) {
            this.$store.commit('Companies/add', response.data);
            this.companies.push(response.data);
          } else {
            this.$store.commit('Companies/update', response.data);

            let index = this.companies.findIndex(item => {
              return item.identifier === this.editingIdentifier;
            });

            this.companies[index] = response.data;
          }

          this.processTariff(response.data);
          this.cancelAddCompany();
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            "Sorry, we couldn't save the company for you"
          );

          this.saving = false;
        });
    },

    processTariff(company) {
      // If the cross hire tariff has been removed, but had previously been saved then we need to remove it.
      if (!this.hasCrossHireRates) {
        this.removeTariff();
        return;
      }

      let method = 'post';
      let url = `/hires/tariffs`;

      if (this.crossHireTariffId) {
        method = 'put';
        url = `/hires/tariffs/${this.crossHireTariffId}`;
      }

      const payload = {
        company_id: company.id,
        rates: this.ratesForm.rates,
        storage_daily_rate: this.ratesForm.storage_daily_rate,
        storage_cap: this.ratesForm.storage_cap
      };

      window.Ajax[method](url, payload)
        .then(response => {
          // At this point, the crossHireTariff attachment is still empty.
          // Reload insurers to ensure they reflect the newly created crossHireTariff.
          this.findCompanies();
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            'Sorry, there was an error saving the tariff data'
          );
        });
    },

    removeTariff() {
      if (this.crossHireTariffId) {
        window.Ajax.delete(`/hires/tariffs/${this.crossHireTariffId}`)
          .then(response => {
            this.crossHireTariffId = null;
            this.ratesForm.reset();
            this.ratesForm.rates = [];
            this.findCompanies();
          })
          .catch(err => {
            window.toastify.error(
              'Error',
              'Sorry, there was an issue deleting the tariff.'
            );
          });
      }
    },

    viewCompany(company) {
      this.readonly = true;

      this.openModal(company);
    },

    updateCompany(company) {
      this.readonly = false;

      this.openModal(company);
    },

    openModal(company) {
      this.form.name = company.name;
      this.form.email_address = company.email_address;
      this.form.telephone_number = company.telephone_number;
      this.form.address_line_one = company.address_line_one;
      this.form.address_line_two = company.address_line_two;
      this.form.city = company.city;
      this.form.postcode = company.postcode;
      this.form.types = company.types;
      this.form.exclude_from_non_fault = company.exclude_from_non_fault;
      this.form.cross_hire_rates = company.cross_hire_rates || [];
      this.editingIdentifier = company.identifier;
      this.hasCrossHireRates =
        !!company.cross_hire_rates.length || !!company.crossHireTariff;

      this.crossHireTariffId = company.crossHireTariff?.identifier || null;

      const rates = company.crossHireTariff?.rates || [];

      if (rates.length) {
        // If the company has a cross hire tariff with rates then pre-populate the form with those rates
        this.ratesForm.rates = rates.map(tariff => ({
          charge_id: tariff.charge_id,
          group_id: tariff.abi_group_id,
          charge_type_id: tariff.charge_type_id,
          rate: tariff.rate
        }));

        this.ratesForm.storage_daily_rate =
          company.crossHireTariff.storage_daily_rate || null;
        this.ratesForm.storage_cap =
          company.crossHireTariff.storage_cap || null;
      } else if (company?.crossHireTariff) {
        // Pre-populate with non-GTA rates if no database rates
        this.ratesForm.rates = [...this.nonGtaRate];
        this.ratesForm.storage_daily_rate = null;
        this.ratesForm.storage_cap = null;
      } else if (company.cross_hire_rates.length) {
        // If the company has legacy cross hire rates instead of a tariff then start with the non-GTA tariff
        this.ratesForm.rates = [...this.nonGtaRate];

        // Then set the company's cross hire rates for the hire rates
        company.cross_hire_rates.forEach(crossHireRate => {
          const rate = this.ratesForm.rates.find(
            r => r.group_id === crossHireRate.group_id
          );

          if (rate) {
            rate.rate = crossHireRate.rate;
          }
        });

        this.ratesForm.storage_daily_rate = null;
        this.ratesForm.storage_cap = null;
      } else {
        this.ratesForm.reset();
        this.ratesForm.rates = [];
      }

      this.openForm();
    },

    toggleRate() {
      if (this.hasCrossHireRates) {
        this.ratesForm.rates = [...this.nonGtaRate];
        this.ratesForm.storage_daily_rate = null;
        this.ratesForm.storage_cap = null;
      } else {
        this.ratesForm.reset();
        this.ratesForm.rates = [];
      }
    },

    fetchRates() {
      this.fetchTariff('non_gta', 'nonGtaRate');
    },

    fetchTariff(type, stateKey) {
      window.Ajax.get(`/hires/tariffs?filter[type]=${type}`)
        .then(response => {
          const tariffs = response.data;

          this[stateKey] = tariffs
            .filter(tariff => tariff.name === type)
            .flatMap(tariff =>
              tariff.rates.map(rate => ({
                charge_id: rate.charge_id,
                group_id: rate.abi_group_id || null,
                charge_type_id: rate.charge_type_id,
                rate: rate.rate
              }))
            );
        })
        .catch(error => {
          console.error(error);
          window.toastify.error('Error', `Failed to fetch ${type} tariffs.`);
        });
    },

    findCompanies() {
      this.loading = true;

      window.Ajax.get(
        `/companies?parts=crossHireTariff&filter[type_id]=${this.filters.type_id}`
      )
        .then(response => {
          this.loading = false;
          this.companies = response.data;
        })
        .catch(err => {});
    },

    sendToCreateView() {
      this.$router.push('/companies/search');
    },

    viewContacts(company) {
      this.$router.push(`/companies/${company.identifier}/contacts`);
    },

    setType(type) {
      this.filters.type_id = type;

      this.findCompanies();
    }
  },

  watch: {
    hasCrossHireRates(value) {
      if (value) {
        if (!this.form?.cross_hire_rates?.length) {
          this.form.cross_hire_rates = this.groups
            .filter(group => group.name.startsWith('B'))
            .map(group => ({
              group_id: group.id,
              rate: null
            }));
        }
      } else {
        this.form.cross_hire_rates = [];
      }
    },

    groups: {
      handler(groups) {
        this.abiGroupsMap = {};

        if (groups?.length) {
          groups.forEach(group => {
            this.abiGroupsMap[group.id] = group.name;
          });
        }
      },
      immediate: true
    }
  }
};
</script>
